import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import i18n from '../../i18n';

const COMM_INBOUND_URL = 'https://api.wiseclean.ee/api:kjtchkL8/comm/web_inbound';

const STATUS = {
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error',
};

const CommReply = () => {
    const { t } = useTranslation();
    const { lang, orderId } = useParams();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState(STATUS.LOADING);
    // Guard against React 18 StrictMode double-invoking the effect in dev.
    const submittedRef = useRef(false);

    // Normalize lang: only en/et are supported, anything else falls back to en.
    const normalizedLang = ['en', 'et'].includes(lang) ? lang : 'en';

    useEffect(() => {
        i18n.changeLanguage(normalizedLang);
    }, [normalizedLang]);

    useEffect(() => {
        if (submittedRef.current) return;
        submittedRef.current = true;

        const event = searchParams.get('event') || '';
        const payload = searchParams.get('payload') || '';

        const submit = async () => {
            try {
                // Form-encoded keeps this a CORS "simple request" (no preflight).
                const body = new URLSearchParams({
                    order_rec_id: orderId || '',
                    event_key: event,
                    payload,
                    lang: normalizedLang,
                });

                const response = await fetch(COMM_INBOUND_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: body.toString(),
                });

                // Endpoint returns 200 + { ok: true }; idempotent on refresh.
                if (response.status !== 200) throw new Error(`Unexpected status ${response.status}`);

                setStatus(STATUS.SUCCESS);
            } catch (error) {
                console.error('comm/web_inbound failed:', error);
                setStatus(STATUS.ERROR);
            }
        };

        submit();
    }, [orderId, searchParams, normalizedLang]);

    const messageByStatus = {
        [STATUS.LOADING]: t('commReplySending'),
        [STATUS.SUCCESS]: t('commReplyThankYou'),
        [STATUS.ERROR]: t('commReplyError'),
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <img
                    src="/wise_clean_logo.png"
                    alt="WiseClean"
                    style={styles.logo}
                />
                <p style={styles.message}>{messageByStatus[status]}</p>
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#F7F7FA',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: '"DM Sans", Arial, sans-serif',
    },
    card: {
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        boxShadow: '0 8px 24px rgba(26, 26, 46, 0.08)',
        borderTop: '4px solid #6DCBDE',
        padding: '40px 32px',
        maxWidth: '420px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
    },
    logo: {
        height: '56px',
        objectFit: 'contain',
        marginBottom: '24px',
    },
    message: {
        margin: 0,
        color: '#1A1A2E',
        fontSize: '18px',
        fontWeight: 500,
        lineHeight: 1.5,
    },
};

export default CommReply;
